import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";

/* ================= CSV EXPORT ================= */
const exportVisibleColumnsToCSV = (
  table,
  rows,
  fileName = "Deposit_Report.csv"
) => {
  const EXCLUDED_KEYS = [];

  const exportableColumns = table
    .getAllLeafColumns()
    .filter(
      (col) =>
        col.getIsVisible() &&
        col.columnDef.accessorKey &&
        !EXCLUDED_KEYS.includes(col.columnDef.accessorKey)
    );

  const headers = exportableColumns.map((col) => col.columnDef.header);

  const csvRows = rows.map((row) =>
    exportableColumns.map((col) => `"${row[col.columnDef.accessorKey] ?? ""}"`)
  );

  const csvContent = [
    headers.join(","),
    ...csvRows.map((r) => r.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};
/* ============================================== */

/* ================= DEBOUNCE HOOK ================= */
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};
/* ================================================ */

const DepositReport = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: selectedCenter,
    studentName: "",
    parentName: "",
    email: "",
    phone: "",
  });

  const debouncedFilters = useDebounce(filters);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  /* ================= TABLE COLUMNS ================= */
  const columns = useMemo(
    () => [
      {
        accessorFn: (_, index) => index + 1,
        header: "S.NO",
        size: 40,
      },
      { accessorKey: "centerName", header: "Centre" },
      { accessorKey: "studentCode", header: "Student Code" },
      { accessorKey: "studentName", header: "Student Name" },
      { accessorKey: "parentName", header: "Parent Name" },
      { accessorKey: "parentEmail", header: "Email" },
      { accessorKey: "parentPhone", header: "Phone" },
      {
        accessorKey: "depositBalance",
        header: "Deposit Balance",
        Cell: ({ cell }) => cell.getValue() ?? 0,
      },
      {
        accessorKey: "registrationBalance",
        header: "Reg Balance",
        Cell: ({ cell }) => cell.getValue() ?? 0,
      },
    ],
    []
  );

  /* ================= THEME ================= */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#e6edf7",
            fontSize: "13px",
            textAlign: "center",
          },
        },
      },
    },
  });

  /* ================= API CALL ================= */
  const getDepositReport = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();

      if (debouncedFilters.centerId)
        params.append("centerId", debouncedFilters.centerId);
      if (debouncedFilters.studentName)
        params.append("studentName", debouncedFilters.studentName);
      if (debouncedFilters.parentName)
        params.append("parent", debouncedFilters.parentName);
      if (debouncedFilters.email)
        params.append("email", debouncedFilters.email);
      if (debouncedFilters.phone)
        params.append("phone", debouncedFilters.phone);

      const response = await api.get(`/getDepositReport?${params.toString()}`);

      setData(response.data?.records || []);
      setSummary(response.data?.summary || null);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch deposit report");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= EFFECTS ================= */
  useEffect(() => {
    setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
  }, [selectedCenter]);

  useEffect(() => {
    if (debouncedFilters.centerId) {
      getDepositReport();
    }
  }, [debouncedFilters]);

  /* ================= HANDLERS ================= */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      centerId: selectedCenter,
      studentName: "",
      parentName: "",
      email: "",
      phone: "",
    });
  };

  /* ================= UI ================= */
  return (
    <div className="container-fluid my-4 px-2">
      <ol className="breadcrumb my-3">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>{" "}
          &gt;
        </li>
        <li>Report Management &gt;</li>
        <li className="active">Deposit Report</li>
      </ol>

      <div className="card">
        <div className="p-2 text-muted" style={{ background: "#f5f7f9" }}>
          Deposit & Registration Balance Report
        </div>
           {/* SUMMARY */}
        {summary && (
          <div className="px-3 mb-3">
            <h6>Summary</h6>
            <div className="d-flex flex-wrap gap-3">
              <span>
                Total Deposit Fee:{" "}
                <strong>{summary.totalDepositFee?.toLocaleString()}</strong>
              </span>
              <span>
                Total Registration Fee:{" "}
                <strong>{summary.totalRegistrationFee?.toLocaleString()}</strong>
              </span>
            </div>
          </div>
        )}
        {/* FILTERS */}
        <div className="container-fluid px-2 py-4">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-3">
              <input
                className="form-control form-control-sm"
                placeholder="Student Name"
                name="studentName"
                value={filters.studentName}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-3">
              <input
                className="form-control form-control-sm"
                placeholder="Parent Name"
                name="parentName"
                value={filters.parentName}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-3">
              <input
                className="form-control form-control-sm"
                placeholder="Email"
                name="email"
                value={filters.email}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-2">
              <input
                className="form-control form-control-sm"
                placeholder="Phone"
                name="phone"
                value={filters.phone}
                onChange={handleFilterChange}
              />
            </div>

            <div className="col-12 col-md-1 d-grid">
              <button className="btn btn-sm btn-border" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* TABLE */}
        <ThemeProvider theme={theme}>
          <MaterialReactTable
            columns={columns}
            data={data}
            state={{ isLoading: loading }}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            initialState={{
              pagination: { pageSize: 50, pageIndex: 0 },
            }}
            renderTopToolbarCustomActions={({ table }) => (
              <button
                className="btn btn-sm btn-border"
                onClick={() =>
                  exportVisibleColumnsToCSV(table, data, "Deposit_Report.csv")
                }
              >
                Export CSV
              </button>
            )}
          />
        </ThemeProvider>
      </div>
    </div>
  );
};

export default DepositReport;
